/**
 * OfficialAgreementLinesEditor — C6.
 * Adds branch-enabled OFFICIAL NDIS lines to a Service Agreement, alongside the existing
 * custom Service Items. Region + cap are inherited from the branch enablement (the backend
 * prices official lines from the branch item), so they are shown read-only; the coordinator
 * sets allocated funding, agreed rate (cap-enforced; $1 locked for Unit-Price=$1) and line dates.
 * Server-side validation (C4) remains authoritative — this only prevents obvious mistakes.
 */
import React from 'react';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { Plus, Trash2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { TrinitiDatePicker } from '@/components/triniti-datetime';

export const emptyOfficialLine = () => ({
  branch_catalogue_item_id: '', pricing_region: '', agreed_rate: '',
  allocated_funding: '', service_start_date: '', service_end_date: '',
});

const num = (v) => (v === '' || v == null ? null : Number(v));
export const pricingMode = (it) => (it?.unit_price_one ? 'Unit Price = $1' : it?.is_quotable ? 'Quotable' : 'Priced');
export const fmtMoney = (v) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);

export function validateOfficialLines(lines, enabled, agr) {
  const byId = Object.fromEntries((enabled || []).map((e) => [e.id, e]));
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]; const it = byId[l.branch_catalogue_item_id];
    const n = i + 1;
    if (!l.branch_catalogue_item_id) return `Official line ${n}: select an NDIS item`;
    if (!it) return `Official line ${n}: the selected item is no longer available`;
    const alloc = num(l.allocated_funding);
    if (alloc == null || isNaN(alloc) || alloc <= 0) return `Official line ${n}: allocated funding must be a positive amount`;
    const cap = it.price_cap != null ? Number(it.price_cap) : null;
    if (!it.unit_price_one) {
      const rate = num(l.agreed_rate);
      if (it.is_quotable && rate == null) return `Official line ${n}: quotable item requires an agreed rate`;
      if (rate != null && rate < 0) return `Official line ${n}: agreed rate cannot be negative`;
      if (rate != null && cap != null && rate > cap) return `Official line ${n}: agreed rate exceeds the ${it.pricing_region || ''} cap of ${fmtMoney(cap)}`;
    }
    if (l.service_start_date && l.service_end_date && l.service_start_date > l.service_end_date)
      return `Official line ${n}: line start date is after its end date`;
    if (agr?.start && l.service_start_date && l.service_start_date < agr.start)
      return `Official line ${n}: line start is before the agreement start`;
    if (agr?.end && l.service_end_date && l.service_end_date > agr.end)
      return `Official line ${n}: line end is after the agreement end`;
  }
  return null;
}

export function officialLinesPayload(lines, enabled) {
  const byId = Object.fromEntries((enabled || []).map((e) => [e.id, e]));
  return lines.map((l) => {
    const it = byId[l.branch_catalogue_item_id] || {};
    return {
      branch_catalogue_item_id: l.branch_catalogue_item_id,
      pricing_region: l.pricing_region || it.pricing_region || undefined,
      agreed_rate: it.unit_price_one ? undefined : (l.agreed_rate === '' ? undefined : String(l.agreed_rate)),
      allocated_funding: l.allocated_funding === '' ? undefined : String(l.allocated_funding),
      service_start_date: l.service_start_date || undefined,
      service_end_date: l.service_end_date || undefined,
    };
  });
}

export function LineFields({ idx, line, item, onChange }) {
  const set = (patch) => onChange({ ...line, ...patch });
  const t = (s) => `agreement-official-${idx}-${s}`;
  const cap = item?.price_cap != null ? Number(item.price_cap) : null;
  const rate = num(line.agreed_rate);
  const overCap = !item?.unit_price_one && rate != null && cap != null && rate > cap;
  const needRate = item?.is_quotable && !item?.unit_price_one && (line.agreed_rate === '' || line.agreed_rate == null);
  return (
    <div className="space-y-3">
      {item && (
        <div className="rounded-md bg-muted/40 px-3 py-2 text-xs grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={t('meta')}>
          <div><p className="text-muted-foreground">Item number</p><p className="font-mono">{item.support_item_number}</p></div>
          <div className="md:col-span-3"><p className="text-muted-foreground">Name</p><p className="line-clamp-1">{item.support_item_name}</p></div>
          <div><p className="text-muted-foreground">Unit</p><p>{item.unit || '—'}</p></div>
          <div><p className="text-muted-foreground">Pricing mode</p><Badge variant="outline" className="text-[10px]">{pricingMode(item)}</Badge></div>
          <div><p className="text-muted-foreground">Region</p><p data-testid={t('region')}>{item.pricing_region || (item.is_quotable ? 'Quote' : '—')}</p></div>
          <div><p className="text-muted-foreground">Applicable cap</p><p className="font-mono" data-testid={t('cap')}>{item.unit_price_one ? '$1.00' : fmtMoney(cap)}</p></div>
          <div><p className="text-muted-foreground">Catalogue effective</p><p>{item.effective_start || item.start_date || '—'} → {item.effective_end_open ? 'open' : (item.effective_end || item.end_date || '—')}</p></div>
          <div><p className="text-muted-foreground">Branch status</p><Badge variant="outline" className={item.enabled ? 'border-green-300 text-green-700 bg-green-50 text-[10px]' : 'text-[10px]'}>{item.enabled ? 'Enabled' : 'Disabled'}</Badge></div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label>Agreed rate {item?.is_quotable && !item?.unit_price_one ? '*' : ''}</Label>
          <Input type="number" step="0.01" min="0" disabled={!!item?.unit_price_one}
            value={item?.unit_price_one ? '1' : line.agreed_rate}
            onChange={(e) => set({ agreed_rate: e.target.value })}
            placeholder={item?.unit_price_one ? 'Fixed $1' : (item?.price_cap != null ? `≤ ${fmtMoney(cap)}` : 'e.g. 80.00')}
            data-testid={t('rate')} />
          {overCap && <p className="text-xs text-red-600" data-testid={t('rate-error')}>Exceeds cap {fmtMoney(cap)}</p>}
          {needRate && <p className="text-xs text-amber-600">Quotable item needs a rate before activation</p>}
        </div>
        <div className="space-y-1">
          <Label>Allocated funding *</Label>
          <Input type="number" step="0.01" min="0" value={line.allocated_funding}
            onChange={(e) => set({ allocated_funding: e.target.value })} placeholder="e.g. 5000.00" data-testid={t('funding')} />
        </div>
        <div className="space-y-1">
          <Label>Line start</Label>
          <TrinitiDatePicker value={line.service_start_date} onChange={(v) => set({ service_start_date: v })} testId={t('start')} />
        </div>
        <div className="space-y-1">
          <Label>Line end</Label>
          <TrinitiDatePicker value={line.service_end_date} onChange={(v) => set({ service_end_date: v })} testId={t('end')} />
        </div>
      </div>
    </div>
  );
}

export function OfficialAgreementLinesEditor({ lines, onChange, enabled, agreementStart, agreementEnd, loading }) {
  const byId = Object.fromEntries((enabled || []).map((e) => [e.id, e]));
  const update = (i, next) => onChange(lines.map((l, j) => (j === i ? next : l)));
  const remove = (i) => onChange(lines.filter((_, j) => j !== i));
  const add = () => onChange([...lines, { ...emptyOfficialLine(), service_start_date: agreementStart || '', service_end_date: agreementEnd || '' }]);
  const total = lines.reduce((s, l) => s + (num(l.allocated_funding) || 0), 0);
  const options = enabled || [];
  return (
    <div className="space-y-3" data-testid="agreement-official-lines">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Official NDIS lines <span className="text-muted-foreground font-normal">({lines.length})</span></Label>
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={loading || options.length === 0} data-testid="agreement-official-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add official line
        </Button>
      </div>
      {loading ? (
        <p className="text-xs text-muted-foreground" data-testid="agreement-official-loading">Loading branch-enabled items…</p>
      ) : options.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="agreement-official-none-enabled">
          No branch-enabled official items for this branch. Enable items in Branch Catalogue first.
        </p>
      ) : lines.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="agreement-official-empty">No official lines yet — add branch-enabled NDIS items here.</p>
      ) : null}
      {lines.map((l, i) => (
        <div key={i} className="rounded-md border p-3 space-y-3" data-testid={`agreement-official-line-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">Official line {i + 1}</span>
            <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} data-testid={`agreement-official-${i}-remove`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="space-y-1">
            <Label>NDIS item (branch-enabled) *</Label>
            <LineItemSelect value={l.branch_catalogue_item_id}
              options={options.map((o) => ({ value: o.id, label: `${o.support_item_number} — ${o.support_item_name || ''}`, keywords: o.registration_group || '' }))}
              onChange={(v) => {
                const it = byId[v] || {};
                update(i, { ...l, branch_catalogue_item_id: v, pricing_region: it.pricing_region || '', agreed_rate: it.unit_price_one ? '1' : (it.agreed_rate != null ? String(it.agreed_rate) : '') });
              }}
              placeholder="Select a branch-enabled official item" testId={`agreement-official-${i}-item`} />
          </div>
          {l.branch_catalogue_item_id && <LineFields idx={i} line={l} item={byId[l.branch_catalogue_item_id]} onChange={(next) => update(i, next)} />}
        </div>
      ))}
      {lines.length > 0 && (
        <div className="flex justify-end text-sm" data-testid="agreement-official-total">
          <span className="text-muted-foreground mr-2">Official lines funding:</span>
          <span className="font-semibold font-mono">{fmtMoney(total)}</span>
        </div>
      )}
    </div>
  );
}

export default OfficialAgreementLinesEditor;
